import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from './api';
import type { ModuleKey } from './moduleTitle';
import { SHELL_MODULE_REGISTRY, shellNavLabel } from './moduleTitle';
import { canOpenModule, hasModuleGrant } from './biModuleKeys';
import { canOpenAdmin } from './foundationFilters';
import { moduleAvatar } from './moduleAvatars';

type Locale = 'hr' | 'en';
type Task = { id: number; title: string; due_date: string | null; priority: string };
type MyTasks = { overdue: Task[]; today: Task[]; upcoming: Task[]; no_date: Task[] };

const INK = '#172033';
const MUT = '#68718a';
const BORDER = '#e3e8ef';
const CARD: React.CSSProperties = { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14 };
const MAX_SHORTCUTS = 10;

function greeting(locale: Locale): string {
    const h = new Date().getHours();
    if (locale === 'en') return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
    return h < 12 ? 'Dobro jutro' : h < 18 ? 'Dobar dan' : 'Dobra večer';
}

function fmtDue(due: string | null): string {
    if (!due) return '';
    return due.slice(0, 10).split('-').reverse().join('.');
}

function Kpi({ accent, value, label, sub }: { accent: string; value: React.ReactNode; label: string; sub?: string }) {
    return (
        <div style={{ ...CARD, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, borderTop: `3px solid ${accent}` }}>
            <div style={{ fontSize: '1.7rem', fontWeight: 800, color: INK, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: MUT, fontWeight: 650 }}>{label}</div>
            {sub ? <div style={{ fontSize: '0.72rem', color: accent, fontWeight: 700 }}>{sub}</div> : null}
        </div>
    );
}

export function HrmsDashboard({ token, locale, user, companiesCount, onNavigate, onShortcutsChange }: {
    token: string;
    locale: Locale;
    user: { name?: string; email?: string; role?: string; allowed_modules?: string[]; landing_shortcuts?: string[] };
    companiesCount: number;
    onNavigate: (key: ModuleKey) => void;
    onShortcutsChange?: (next: string[]) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tasks, setTasks] = useState<MyTasks | null>(null);
    const [query, setQuery] = useState('');
    const [shortcuts, setShortcuts] = useState<string[]>(user.landing_shortcuts ?? []);
    const [editShortcuts, setEditShortcuts] = useState(false);

    useEffect(() => {
        void api<MyTasks>('/tasks/my-tasks', token).then(setTasks).catch(() => setTasks(null));
    }, [token]);

    const modules = useMemo(() => SHELL_MODULE_REGISTRY.filter((e) => {
        if (e.key === 'dashboard') return false;
        if (e.key === 'knjigovodstvo') {
            return (user.email ?? '').toLowerCase() === 'vjakomin@gmail.com';
        }
        if (e.key === 'poslovnice') {
            return user.role === 'administrator' || user.role === 'superadministrator';
        }
        if (e.key === 'admin') return canOpenAdmin({ ...user, role: user.role ?? '' }) && canOpenModule(user, 'admin');
        const canOpenThis = canOpenModule(user, e.key);
        if (!e.hiddenFromNav) return canOpenThis;
        if (!e.parentKey || !hasModuleGrant(user, e.key)) return false;
        return !canOpenModule(user, e.parentKey);
    }), [user]);

    const persistShortcuts = useCallback((next: string[]) => {
        setShortcuts(next);
        onShortcutsChange?.(next);
        void mutate('/landing/shortcuts', token, 'PUT', { shortcuts: next }).catch(() => undefined);
    }, [token, onShortcutsChange]);

    const toggleShortcut = useCallback((key: string) => {
        setShortcuts((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key].slice(0, MAX_SHORTCUTS);
            onShortcutsChange?.(next);
            void mutate('/landing/shortcuts', token, 'PUT', { shortcuts: next }).catch(() => undefined);
            return next;
        });
    }, [token, onShortcutsChange]);

    const validShortcuts = shortcuts.filter((k) => modules.some((m) => m.key === k));
    const overdue = tasks?.overdue ?? [];
    const today = tasks?.today ?? [];
    const upcoming = tasks?.upcoming ?? [];
    const openTasks = overdue.length + today.length + upcoming.length;

    const taskRows: Array<{ task: Task; tone: 'overdue' | 'today' | 'soon' }> = [
        ...overdue.map((task) => ({ task, tone: 'overdue' as const })),
        ...today.map((task) => ({ task, tone: 'today' as const })),
        ...upcoming.map((task) => ({ task, tone: 'soon' as const })),
    ].slice(0, 8);

    const toneColor = { overdue: '#c23a3a', today: '#b7791f', soon: '#3b5bdb' };
    const toneLabel = { overdue: t('kasni', 'overdue'), today: t('danas', 'today'), soon: t('uskoro', 'soon') };

    const filteredModules = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return modules;
        return modules.filter((e) =>
            e.key.includes(q)
            || String(e.code).startsWith(q)
            || shellNavLabel(e, 'hr').toLowerCase().includes(q)
            || shellNavLabel(e, 'en').toLowerCase().includes(q));
    }, [modules, query]);

    const dateStr = new Date().toLocaleDateString(locale === 'en' ? 'en-GB' : 'hr-HR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const firstName = (user.name || user.email || '').split(' ')[0];

    return (
        <div className="dash2" style={{ display: 'flex', flexDirection: 'column', gap: 16, color: INK }}>
            <style>{`
                .dash2 .dash2-tile { transition: border-color .12s ease, box-shadow .12s ease, transform .12s ease; }
                .dash2 .dash2-tile:hover { border-color: #b9c3d4; box-shadow: 0 4px 14px rgba(23,32,51,0.08); transform: translateY(-1px); }
                .dash2 .dash2-star { opacity: 0; transition: opacity .12s ease; }
                .dash2 .dash2-cell:hover .dash2-star, .dash2 .dash2-star.on, .dash2 .dash2-star:focus-visible { opacity: 1; }
                .dash2 button:focus-visible, .dash2 input:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
                .dash2 input::placeholder { color: #9aa3b5; }
                @media (max-width: 860px) {
                    .dash2 .dash2-cols { grid-template-columns: 1fr !important; }
                    .dash2 .dash2-hero-actions { flex-wrap: wrap; }
                }
            `}</style>

            {/* Hero: pozdrav + status zadataka */}
            <section style={{ background: INK, color: '#fff', borderRadius: 16, padding: '22px 24px', position: 'relative', overflow: 'hidden' }}>
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 45%)' }} />
                <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: '#c9a24b' }} />
                <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.76rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontWeight: 650 }}>{dateStr}</div>
                        <div style={{ fontSize: '1.55rem', fontWeight: 800, marginTop: 4 }}>{greeting(locale)}, {firstName}</div>
                        <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', marginTop: 6 }}>
                            {tasks === null
                                ? t('Učitavam zadatke…', 'Loading tasks…')
                                : openTasks > 0
                                    ? t(`${openTasks} otvorenih zadataka${overdue.length ? `, ${overdue.length} s probijenim rokom` : ''}.`, `${openTasks} open tasks${overdue.length ? `, ${overdue.length} overdue` : ''}.`)
                                    : t('Nema otvorenih zadataka.', 'No open tasks.')}
                        </div>
                    </div>
                    <div className="dash2-hero-actions" style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => onNavigate('team_tasks' as ModuleKey)}
                            style={{ border: '1px solid rgba(255,255,255,0.35)', background: '#fff', color: INK, fontWeight: 700, padding: '9px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13 }}>
                            {t('Zadaci', 'Tasks')}
                        </button>
                        {canOpenModule(user, 'tasks2') ? (
                            <button type="button" onClick={() => onNavigate('tasks2' as ModuleKey)}
                                style={{ border: '1px solid rgba(255,255,255,0.35)', background: 'transparent', color: '#fff', fontWeight: 700, padding: '9px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13 }}>
                                {t('Zadaci v2', 'Tasks v2')}
                            </button>
                        ) : null}
                    </div>
                </div>
            </section>

            {/* KPI traka */}
            <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                <Kpi accent="#3b5bdb" value={companiesCount} label={t('Tvrtke u grupi', 'Companies')} />
                <Kpi accent="#1f7a36" value={tasks ? openTasks : '—'} label={t('Otvoreni zadaci', 'Open tasks')} />
                <Kpi accent="#c23a3a" value={tasks ? overdue.length : '—'} label={t('Probijeni rokovi', 'Overdue')} sub={overdue.length ? t('traži pažnju', 'needs attention') : undefined} />
                <Kpi accent="#c9a24b" value={modules.length} label={t('Dostupni moduli', 'Available modules')} />
            </section>

            {/* Zadaci + prečaci */}
            <section className="dash2-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 12 }}>
                <div style={{ ...CARD, padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
                        <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800 }}>{t('Neposredni zadaci', 'Immediate tasks')}</h3>
                        <button type="button" onClick={() => onNavigate('team_tasks' as ModuleKey)}
                            style={{ border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 650, padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                            {t('Svi zadaci', 'All tasks')} →
                        </button>
                    </div>
                    {taskRows.length === 0 ? (
                        <div style={{ color: MUT, padding: '18px 0', textAlign: 'center', fontSize: '0.86rem' }}>
                            {tasks ? t('Nema neposrednih zadataka.', 'No immediate tasks.') : t('Učitavam…', 'Loading…')}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {taskRows.map(({ task, tone }) => (
                                <button key={task.id} type="button" onClick={() => onNavigate('team_tasks' as ModuleKey)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 10, border: `1px solid ${tone === 'overdue' ? '#f0d4d4' : BORDER}`, background: tone === 'overdue' ? '#fdf6f6' : '#fafbfd', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                                    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: toneColor[tone], flex: 'none' }} />
                                    <span style={{ flex: 1, minWidth: 0, fontWeight: 650, color: INK, fontSize: '0.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
                                    <span style={{ fontSize: '0.72rem', color: toneColor[tone], fontWeight: 700, flex: 'none' }}>
                                        {toneLabel[tone]}{task.due_date ? ` · ${fmtDue(task.due_date)}` : ''}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{ ...CARD, padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
                        <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800 }}>{t('Brzi pristup', 'Quick access')}</h3>
                        {validShortcuts.length > 0 ? (
                            <button type="button" onClick={() => setEditShortcuts((v) => !v)}
                                style={{ border: `1px solid ${editShortcuts ? INK : BORDER}`, background: editShortcuts ? INK : '#fff', color: editShortcuts ? '#fff' : INK, fontWeight: 650, padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                                {editShortcuts ? t('Gotovo', 'Done') : t('Uredi', 'Edit')}
                            </button>
                        ) : null}
                    </div>
                    {validShortcuts.length === 0 ? (
                        <div style={{ color: MUT, padding: '14px 0', fontSize: '0.84rem' }}>
                            {t('Označi ★ na modulu dolje da ga dodaš u brzi pristup.', 'Mark ★ on a module below to add it to quick access.')}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                            {validShortcuts.map((key) => {
                                const av = moduleAvatar(key);
                                const e = SHELL_MODULE_REGISTRY.find((m) => m.key === key);
                                return (
                                    <div key={key} style={{ position: 'relative' }}>
                                        <button type="button" onClick={() => (editShortcuts ? persistShortcuts(validShortcuts.filter((k) => k !== key)) : onNavigate(key as ModuleKey))}
                                            className="dash2-tile"
                                            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: 10, borderRadius: 11, border: `1px solid ${BORDER}`, background: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                                            <span aria-hidden="true" style={{ width: 30, height: 30, minWidth: 30, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', background: `linear-gradient(135deg, ${av.grad[0]}, ${av.grad[1]})` }}>{av.icon}</span>
                                            <span style={{ fontSize: '0.79rem', fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e ? shellNavLabel(e, locale) : key}</span>
                                        </button>
                                        {editShortcuts ? (
                                            <span aria-hidden="true" style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#c23a3a', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>×</span>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {editShortcuts ? (
                        <div style={{ color: MUT, fontSize: '0.74rem', marginTop: 10 }}>{t('Klik na karticu je uklanja. Najviše 10 prečaca.', 'Click a card to remove it. Max 10 shortcuts.')}</div>
                    ) : null}
                </div>
            </section>

            {/* Svi moduli */}
            <section style={{ ...CARD, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800 }}>{t('Svi moduli', 'All modules')} <span style={{ color: MUT, fontWeight: 650, fontSize: '0.8rem' }}>({modules.length})</span></h3>
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('Traži modul…', 'Search modules…')}
                        style={{ border: `1px solid ${BORDER}`, borderRadius: 9, padding: '7px 12px', fontSize: 13, minWidth: 200, color: INK, background: '#fafbfd' }}
                    />
                </div>
                {filteredModules.length === 0 ? (
                    <div style={{ color: MUT, padding: '14px 0', fontSize: '0.86rem' }}>{t('Nijedan modul ne odgovara pretrazi.', 'No modules match the search.')}</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                        {filteredModules.map((e) => {
                            const av = moduleAvatar(e.key);
                            const starred = shortcuts.includes(e.key);
                            return (
                                <div key={e.key} className="dash2-cell" style={{ position: 'relative' }}>
                                    <button type="button" onClick={() => onNavigate(e.key)}
                                        className="dash2-tile"
                                        style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 13px', paddingRight: 36, borderRadius: 12, border: `1px solid ${BORDER}`, background: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                                        <span aria-hidden="true" style={{ width: 38, height: 38, minWidth: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', background: `linear-gradient(135deg, ${av.grad[0]}, ${av.grad[1]})` }}>{av.icon}</span>
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: INK, lineHeight: 1.25 }}>{shellNavLabel(e, locale)}</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`dash2-star${starred ? ' on' : ''}`}
                                        onClick={() => toggleShortcut(e.key)}
                                        title={starred ? t('Ukloni iz brzog pristupa', 'Remove from quick access') : t('Dodaj u brzi pristup', 'Add to quick access')}
                                        aria-label={starred ? t('Ukloni iz brzog pristupa', 'Remove from quick access') : t('Dodaj u brzi pristup', 'Add to quick access')}
                                        style={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: starred ? '#c9a24b' : '#b9c3d4', padding: 4, lineHeight: 1 }}>
                                        {starred ? '★' : '☆'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
